import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { api } from '../client';
import { useAuthStore } from '../../store/authStore';

/**
 * Recursos que vivem dentro de /escolas/:escolaId/...
 * O hook adiciona o prefixo automaticamente.
 */
const ESCOLA_RESOURCES = [
  'alunos',
  'turmas',
  'turnos',
  'professores',
  'materias',
  'equipe',
  'comunicados',
  'chamadas',
  'dispositivos-leitura',
  'presencas-diarias',
  'eventos-acesso',
  'notificacoes-evento',
  'incidentes-operacionais',
  'tags-rfid',
  'horarios',
  'responsaveis',
];

/** Recursos cujo update na API é `PATCH` (Nest), não `PUT`. */
const PATCH_UPDATE_RESOURCES = new Set(['dispositivos-leitura', 'responsaveis', 'equipe', 'turnos', 'professores']);

/** GET que devolve array cru, `{ data: [] }` ou `{ items: [], total, ... }` (paginação). */
function normalizeListPayload(data: unknown): unknown[] {
  if (Array.isArray(data)) return data;
  if (data && typeof data === 'object') {
    const o = data as Record<string, unknown>;
    if (Array.isArray(o.data)) return o.data;
    if (Array.isArray(o.items)) return o.items;
  }
  return [];
}

/**
 * Hook genérico para CRUD de qualquer recurso.
 * Detecta automaticamente se o recurso é nested em /escolas/:escolaId/
 * ou se é um recurso de nível raiz (responsaveis, admin/*, escolas, etc.).
 */
export function useCrud<T extends { id: string }>(
  resource: string,
  filters?: Record<string, string>,
  options?: { enabled?: boolean },
) {
  const queryClient = useQueryClient();
  const escolaId = useAuthStore((s) => s.escolaId);

  // Monta o path real — se o resource é da escola, adiciona o prefixo
  const basePath = ESCOLA_RESOURCES.includes(resource) && escolaId
    ? `/escolas/${escolaId}/${resource}`
    : `/${resource}`;

  const query = useQuery({
    queryKey: [resource, escolaId, filters],
    queryFn: async () => {
      const { data } = await api.get(basePath, { params: filters });
      return normalizeListPayload(data) as T[];
    },
    // Não busca se precisa de escolaId mas não tem
    enabled:
      (options?.enabled ?? true) &&
      !(ESCOLA_RESOURCES.includes(resource) && !escolaId),
  });

  // Atualização transparente quando qualquer modal ou diálogo é fechado
  useEffect(() => {
    const handleGlobalRefetch = () => {
      queryClient.invalidateQueries({ queryKey: [resource] });
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('ac-data-refetch', handleGlobalRefetch);
      return () => window.removeEventListener('ac-data-refetch', handleGlobalRefetch);
    }
  }, [queryClient, resource]);

  const create = useMutation({
    mutationFn: async (payload: Partial<T>) => { const { data } = await api.post(basePath, payload); return data; },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [resource] });
      window.dispatchEvent(new CustomEvent('ac-data-refetch'));
    },
  });

  const update = useMutation({
    mutationFn: ({ id, ...payload }: Partial<T> & { id: string }) =>
      PATCH_UPDATE_RESOURCES.has(resource)
        ? api.patch(`${basePath}/${id}`, payload)
        : api.put(`${basePath}/${id}`, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [resource] });
      window.dispatchEvent(new CustomEvent('ac-data-refetch'));
    },
  });

  const deactivate = useMutation({
    mutationFn: (id: string) => api.delete(`${basePath}/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [resource] });
      window.dispatchEvent(new CustomEvent('ac-data-refetch'));
    },
  });

  return {
    data: (query.data ?? []) as T[],
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    create,
    update,
    deactivate,
  };
}
